import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Award, ExternalLink, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { usePendingActions } from '../../context/PendingActionsContext';
import {
  markCertificateClaimToastShown,
  shouldShowCertificateClaimToast,
} from '../../lib/certificateClaimToastStorage';
import {
  trackCertificateNudgeToastCtaClick,
  trackCertificateNudgeToastDismissed,
  trackCertificateNudgeToastShown,
} from '../../lib/analytics';

function greetingFirstName(name: string | undefined): string {
  const raw = (name ?? '').trim();
  if (!raw) return 'there';
  const clean = raw.includes('@') ? (raw.split('@')[0] ?? raw) : raw;
  const first = clean.split(/\s+/)[0] ?? clean;
  if (!first) return 'there';
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/** Celebratory card on `/` — shown at most once per 24h when a certificate is pending. */
export function CertificateClaimToast() {
  const { user } = useApp();
  const { pendingCertificates, loading } = usePendingActions();
  const [visible, setVisible] = useState(false);
  const shownRef = useRef(false);

  const primaryAction = pendingCertificates[0] ?? null;
  const congratulationLabel = useMemo(
    () => `Congratulations, ${greetingFirstName(user?.name)}!`,
    [user?.name],
  );

  useEffect(() => {
    if (loading || !primaryAction || shownRef.current) return;
    if (!shouldShowCertificateClaimToast()) return;

    shownRef.current = true;
    markCertificateClaimToastShown();
    setVisible(true);
    trackCertificateNudgeToastShown(primaryAction.projectId, primaryAction.certificateAccess);
  }, [loading, primaryAction]);

  const dismiss = useCallback(() => {
    if (primaryAction) {
      trackCertificateNudgeToastDismissed(primaryAction.projectId);
    }
    setVisible(false);
  }, [primaryAction]);

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        dismiss();
      }
    },
    [dismiss],
  );

  const handleCtaClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
      if (!primaryAction) return;
      trackCertificateNudgeToastCtaClick(
        primaryAction.projectId,
        primaryAction.certificateAccess,
      );
      setVisible(false);
    },
    [primaryAction],
  );

  if (!visible || !primaryAction) return null;

  return (
    <div
      role="presentation"
      data-testid="certificate-claim-toast-backdrop"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 150,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div
        role="dialog"
        aria-labelledby="cert-claim-toast-greeting cert-claim-toast-title"
        aria-describedby="cert-claim-toast-desc"
        data-testid="certificate-claim-toast"
        onClick={dismiss}
        style={{
          width: '100%',
          maxWidth: 440,
          background: '#fff',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          padding: '22px 22px 20px',
          position: 'relative',
        }}
      >
        <button
          type="button"
          aria-label="Dismiss"
          data-testid="certificate-claim-toast-close"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'none',
            border: 'none',
            padding: 4,
            color: '#94a3b8',
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          <X size={18} aria-hidden />
        </button>

        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'rgba(20, 184, 166, 0.12)',
            color: 'var(--primary-dark)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
          }}
        >
          <Award size={26} aria-hidden />
        </div>

        <p
          id="cert-claim-toast-greeting"
          style={{
            margin: '0 0 6px',
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '0.01em',
            color: 'var(--primary-dark)',
          }}
        >
          {congratulationLabel}
        </p>
        <h2
          id="cert-claim-toast-title"
          style={{
            margin: '0 0 8px',
            fontSize: 17,
            fontWeight: 700,
            color: 'var(--marketing-heading)',
            lineHeight: 1.35,
            paddingRight: 28,
          }}
        >
          {primaryAction.title}
        </h2>
        <p
          id="cert-claim-toast-desc"
          style={{
            margin: '0 0 18px',
            fontSize: 14,
            color: 'var(--marketing-muted)',
            lineHeight: 1.5,
          }}
        >
          {primaryAction.description}
        </p>

        <a
          href={primaryAction.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="certificate-claim-toast-cta"
          onClick={(event) => {
            event.stopPropagation();
            handleCtaClick(event);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 20px',
            borderRadius: 10,
            background: 'var(--gradient-1)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          {primaryAction.ctaLabel}
          <ExternalLink size={16} aria-hidden />
        </a>
      </div>
    </div>
  );
}

export default CertificateClaimToast;
